import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Attach auth token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh on 401
let isRefreshing = false;
let failedQueue: any[] = [];

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const res = await api.post('/auth/refresh');
        const { access_token } = res.data;
        localStorage.setItem('access_token', access_token);
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        failedQueue.forEach((p) => p.resolve(access_token));
        failedQueue = [];
        original.headers.Authorization = `Bearer ${access_token}`;
        return api(original);
      } catch {
        failedQueue.forEach((p) => p.reject(err));
        failedQueue = [];
        localStorage.removeItem('access_token');
        window.location.href = '/auth/login';
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

// ── AUTH ──────────────────────────────────────────────────────
export const authAPI = {
  login: (email: string, password: string) =>
    api.post(`/auth/login`, { email, password }),
  register: (data: any) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh'),
  me: () => api.get('/auth/me'),
  forgotPassword: (email: string, tenant_id?: string) => api.post('/auth/forgot-password', { email, tenant_id }),
  resetPassword: (token: string, password: string) => api.post('/auth/reset-password', { token, password }),
};

// ── TICKETS ───────────────────────────────────────────────────
export const ticketsAPI = {
  list: (params?: any) => api.get('/tickets', { params }),
  get: (id: string) => api.get(`/tickets/${id}`),
  create: (data: any) => api.post('/tickets', data),
  update: (id: string, data: any) => api.patch(`/tickets/${id}`, data),
  addMessage: (id: string, data: any) => api.post(`/tickets/${id}/messages`, data),
  getTimeline: (id: string) => api.get(`/tickets/${id}/timeline`),
  merge: (id: string, targetId: string) => api.post(`/tickets/${id}/merge`, { target_ticket_id: targetId }),
  escalate: (id: string, data: any) => api.post(`/tickets/${id}/escalate`, data),
  reopen: (id: string) => api.post(`/tickets/${id}/reopen`),
  bulkAssign: (ticketIds: string[], agentId: string) => api.post('/tickets/bulk-assign', { ticket_ids: ticketIds, agent_id: agentId }),
};

// ── AI AGENTS ─────────────────────────────────────────────────
export const aiAgentsAPI = {
  list: () => api.get('/ai-agents'),
  get: (id: string) => api.get(`/ai-agents/${id}`),
  create: (data: any) => api.post('/ai-agents', data),
  update: (id: string, data: any) => api.patch(`/ai-agents/${id}`, data),
  clone: (id: string, newName: string) => api.post(`/ai-agents/${id}/clone`, { new_name: newName }),
  chat: (id: string, data: any) => api.post(`/ai-agents/${id}/chat`, data),
  preview: (id: string, message: string) => api.post(`/ai-agents/${id}/preview`, { message }),
  ingest: (id: string, formData: FormData) => api.post(`/ai-agents/${id}/kb/ingest`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  ingestUrls: (id: string, urls: string[], collectionId?: string) => api.post(`/ai-agents/${id}/kb/ingest`, { urls, collection_id: collectionId }),
  ingestionStatus: (id: string, jobId: string) => api.get(`/ai-agents/${id}/kb/status/${jobId}`),
  kbArticles: (id: string, params?: any) => api.get(`/ai-agents/${id}/kb/articles`, { params }),
  createArticle: (id: string, data: any) => api.post(`/ai-agents/${id}/kb/articles`, data),
  analytics: (id: string, params?: any) => api.get(`/ai-agents/${id}/analytics`, { params }),
  conversations: (id: string, params?: any) => api.get(`/ai-agents/${id}/conversations`, { params }),
  feedback: (id: string, data: any) => api.post(`/ai-agents/${id}/feedback`, data),
};

// ── AGENTS ────────────────────────────────────────────────────
export const agentsAPI = {
  list: () => api.get('/agents'),
  workload: () => api.get('/agents/workload'),
  invite: (data: any) => api.post('/agents/invite', data),
  update: (id: string, data: any) => api.patch(`/agents/${id}`, data),
  remove: (id: string) => api.delete(`/agents/${id}`),
};

// ── ANALYTICS ─────────────────────────────────────────────────
export const analyticsAPI = {
  overview: (params?: any) => api.get('/analytics/overview', { params }),
  tickets: (params?: any) => api.get('/analytics/tickets', { params }),
  agents: (params?: any) => api.get('/analytics/agents', { params }),
  csat: (params?: any) => api.get('/analytics/csat', { params }),
};

// ── TEAMS ─────────────────────────────────────────────────────
export const teamsAPI = {
  list: () => api.get('/teams'),
  create: (data: any) => api.post('/teams', data),
  update: (id: string, data: any) => api.patch(`/teams/${id}`, data),
  addAgents: (id: string, agentIds: string[]) => api.post(`/teams/${id}/agents`, { agent_ids: agentIds }),
};

// ── NOTIFICATIONS ─────────────────────────────────────────────
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  markRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
};

// ── SLA ───────────────────────────────────────────────────────
export const slaAPI = {
  list: () => api.get('/sla-policies'),
  create: (data: any) => api.post('/sla-policies', data),
  update: (id: string, data: any) => api.patch(`/sla-policies/${id}`, data),
};

// ── AUTOMATIONS ───────────────────────────────────────────────
export const automationsAPI = {
  list: () => api.get('/automations'),
  create: (data: any) => api.post('/automations', data),
  update: (id: string, data: any) => api.patch(`/automations/${id}`, data),
};

// ── TENANTS (Super Admin) ─────────────────────────────────────
export const superAPI = {
  listTenants: (params?: any) => api.get('/super/tenants', { params }),
  createTenant: (data: any) => api.post('/super/tenants', data),
  updateTenantStatus: (id: string, status: string) => api.patch(`/super/tenants/${id}/status`, { status }),
  impersonate: (userId: string) => api.post(`/auth/super/impersonate/${userId}`),
};

// ── CANNED RESPONSES ──────────────────────────────────────────
export const cannedAPI = {
  list: () => api.get('/canned-responses'),
  create: (data: any) => api.post('/canned-responses', data),
};

// ── ATTACHMENTS ───────────────────────────────────────────────
export const attachmentsAPI = {
  upload: (files: File[]) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return api.post('/attachments/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  link: (attachmentIds: string[], ticketId: string, messageId?: string) =>
    api.patch('/attachments/link', { attachment_ids: attachmentIds, ticket_id: ticketId, message_id: messageId }),
  listForTicket: (ticketId: string) => api.get(`/attachments/ticket/${ticketId}`),
  downloadUrl: (id: string) => `${api.defaults.baseURL}/attachments/download/${id}`,
  remove: (id: string) => api.delete(`/attachments/${id}`),
};
